import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { getDisplayedUnit } from 'commons/src/commonFunctions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import InputInfo from 'commons/src/components/input/InputInfo';
import RadioButtons from 'commons/src/components/input/Radio';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import ThresholdDot from 'commons/src/components/thresholds/ThresholdDot';
import ThresholdSetting from 'commons/src/components/thresholds/ThresholdSetting';
import { roleRestrictions, sensorFullNameKey, sensorUnits } from 'commons/src/constants';
import useHasRequiredRole from 'commons/src/hooks/useHasRequiredRole';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ThresholdRange, RecommendedThresholdOptions } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOptions, ThresholdUpdatePayload } from 'commons/src/models/customThresholdModels';
import { Store } from '../../../reducers';
import styles from './SensorThresholdMgmt.module.scss';
import useSensorThresholdMgmt from './useSensorThresholdMgmt';

export type Props = {
    buttonText: string;
    sensorType: SensorTypes;
    thresholds: {
        customOptions?: CustomOptions;
        ranges: ThresholdRange[];
        unit: keyof typeof sensorUnits;
        thresholdOptions: ThresholdOptions;
    };
    thresholdScope: ThresholdsResourceScope;
    parentThresholdScope?: ThresholdsResourceScope;
    id?: string;
    updateSuccessAction?: (id: string, payload: ThresholdUpdatePayload) => void;
    recommendedThresholdOptions?: RecommendedThresholdOptions[];
    parentUrl?: string;
};
const SensorThresholdMgmt = ({
    sensorType,
    thresholds,
    thresholdScope,
    buttonText,
    parentThresholdScope,
    id,
    updateSuccessAction,
    parentUrl,
    recommendedThresholdOptions,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const allowedToEdit = useHasRequiredRole(roleRestrictions.editThresholds);
    const {
        changeScopeSelection,
        thresholdOptions,
        goToParentThresholds,
        selectedValues,
        selectedScope,
        handleSetSelectedValues,
        handleOnSave,
        isLoading,
        isSaved,
        error,
    } = useSensorThresholdMgmt({
        thresholds,
        thresholdScope,
        sensorType,
        id,
        updateSuccessAction,
        parentUrl,
    });

    const accountCustomThresholdsEnabled = useSelector((state: Store) => state.toggles.accountCustomThresholdsEnabled);

    const radioOptions = [
        {
            value: parentThresholdScope,
            label: `ThresholdSettings.Default_${parentThresholdScope}`,
            customStyle: styles.parentRadioButton,
            ...(accountCustomThresholdsEnabled && {
                buttonChildren: (
                    <div className={styles.editLink}>
                        <TertiaryButton onClick={goToParentThresholds} title="Edit" size="medium" />
                    </div>
                ),
            }),
        },
        { value: thresholdScope, label: `ThresholdSettings.Custom_${thresholdScope}` },
    ];

    const spaceScope = thresholdScope === ThresholdsResourceScope.SPACE;

    const infoContent = recommendedThresholdOptions && (
        <div>
            <strong>{txt('CustomThresholds.RecommendedValues')}:</strong>
            <div className={styles.hintContent}>
                {recommendedThresholdOptions.map(opt => (
                    <div key={opt.type} className={styles.hintItem}>
                        <ThresholdDot type={opt.type} />
                        <span>
                            {txt(`ThresholdType.${opt.type}`)}: {opt.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    const getUnit = (): ReactElement => {
        const unit = getDisplayedUnit(sensorType, thresholds.unit as string);
        return <div className={styles.unit}>({unit})</div>;
    };

    return (
        <div>
            {!spaceScope && (
                <div className={styles.header}>
                    <SectionHeader
                        headerType={4}
                        headerName=""
                        customHeader={
                            <>
                                {txt(sensorFullNameKey(sensorType))} {getUnit()}
                            </>
                        }
                        leadingIcon={<SensorIcon sensor={sensorType} />}
                        key={`thresholdHeader${sensorType}`}
                    />
                    <div className={styles.info}>
                        <InputInfo infoContent={infoContent} infoText="" />
                    </div>
                </div>
            )}
            <p className={styles.description}>{txt(`CustomThresholds.${sensorType}Description`)}</p>
            {parentThresholdScope && (
                <div className={styles.radioButtons}>
                    <RadioButtons
                        row
                        buttons={radioOptions}
                        onChange={changeScopeSelection}
                        labelId={`thresholdHeader${sensorType}`}
                        selectorName="scope"
                        value={selectedScope}
                        disabled={!allowedToEdit}
                    />
                </div>
            )}
            <ThresholdSetting
                sensorType={sensorType}
                ranges={thresholds.ranges}
                thresholdOptions={thresholdOptions}
                customOptions={thresholds.customOptions}
                selectedValues={selectedValues}
                setSelectedValues={handleSetSelectedValues}
                isParentScope={selectedScope === parentThresholdScope}
                scopeModified={selectedScope !== thresholds.thresholdOptions.activeScope}
                buttonText={buttonText}
                loading={isLoading}
                onSave={handleOnSave}
                isSaved={isSaved}
                fullwidthButton={spaceScope}
                allowedToEdit={allowedToEdit}
            />
            {error && (
                <div className={styles.error}>
                    <ResponseBox text={`ErrorCodes.${error.error}`} />
                </div>
            )}
        </div>
    );
};

export default SensorThresholdMgmt;
